import { any, arrayOf, node, shape, string } from "prop-types";
import React from "react";

import Button from "~components/Button";
import DynamicContent from "~modules/DynamicContent";
import Accordion from "~ts_components/Accordion/Accordion";
import DuoColorText from "~ts_components/DuoColorText/DuoColorText";
import ShopCardPreview from "~ts_components/ShopCardPreview/ShopCardPreview";

const ACCOUNT_OWNER_MOCK = [
  {
    id: "first-name",
    label: "Jouw voornaam",
    content: "Fieke",
  },
  {
    id: "last-name",
    label: "Jouw achternaam",
    content: "Flier",
  },
  {
    id: "email",
    label: "Jouw e-mailadres",
    content: "fieke@iamsterdam.com",
  },
];

const CURRENT_SUBSCRIPTION_CONTENT_MOCK = [
  {
    id: "order-number",
    label: "Order nummer",
    content: "1234567890",
  },
  {
    id: "account-creation-date",
    label: "Uitpas houder sinds",
    content: "12/12/2024",
  },
];

const PAYMENT_DETAILS_CONTENT_MOCK = [
  {
    id: "subscription-type",
    label: "Jouw abonnement",
    content: "Digitale Uitpas plus daluren voordeel",
  },
  {
    id: "subscription-next-payment",
    label: "Volgende incassodatum",
    content: "12/12/2024",
  },
  {
    id: "subscription-next-payment-amount",
    label: "Volgende incassobedrag",
    content: "12",
  },
  {
    id: "subscription-actions",
    label: "Abonnement",
    actions: [
      <Button variant="outline" key="cancel" label="Abonnement opzeggen">
        Abonnement opzeggen
      </Button>,
      <Button variant="outline" key="delete" label="Uitpas-account verwijderen">
        Uitpas-account verwijderen
      </Button>,
    ],
  },
];

const InfoSection = ({ title, data }) => (
  <div>
    <h2 className="text-header-4 mb-4">{title}</h2>
    <ul className="flex flex-col gap-y-4">
      {data.map(({ id, label, content, actions }) => (
        <li key={id} className="flex flex-col gap-y-2">
          <span className="text-body-large-bold">{label}</span>
          {content && <span className="text-body">{content}</span>}
          {actions && (
            <div className="flex flex-col gap-y-2 items-start">
              {actions.map((action, idx) => (
                <React.Fragment key={idx}>{action}</React.Fragment>
              ))}
            </div>
          )}
        </li>
      ))}
    </ul>
  </div>
);

InfoSection.propTypes = {
  title: string.isRequired,
  data: arrayOf(
    shape({
      id: string.isRequired,
      label: string.isRequired,
      content: string,
      actions: arrayOf(node),
    }),
  ).isRequired,
};

const AccountSettingsPageTemplate = ({
  accountSettingsPage: { title, content },
}) => {
  return (
    <div className="lg:bg-grey-light max-md:px-2 py-8 md:py-16 -mt-4">
      <h1 className="text-header-1 text-center mb-10">{title}</h1>
      <div className="bg-white max-w-300 mx-auto rounded-lg p-2 lg:p-16">
        {/* Main two-column grid layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-y-4 lg:gap-x-5">
          {/* Left column - Account details */}
          <section className="w-full flex flex-col gap-y-4 order-2 lg:col-span-7 lg:order-1">
            <InfoSection title="Over jou" data={ACCOUNT_OWNER_MOCK} />
            <div className="layout-divider my-2 lg:my-4" />
            <InfoSection
              title="Over jouw Uitpas"
              data={CURRENT_SUBSCRIPTION_CONTENT_MOCK}
            />
            <div className="layout-divider my-2 lg:my-4" />
            <InfoSection
              title="Betalingsinformatie en abonnement"
              data={PAYMENT_DETAILS_CONTENT_MOCK}
            />
          </section>

          {/* Right column - current subscription card */}
          <section className="w-full flex flex-col gap-y-4 order-1 lg:col-span-5 lg:order-2 mb-4">
            <ShopCardPreview
              product={{
                id: "uitpas-plus-daluren",
                title: "Uitpas plus daluren voordeel",
                price: 2.5,
                frequency: "monthly",
              }}
              contentItems={[
                { id: "name", label: "Pashouder", content: "Fieke Flier" },
                {
                  id: "pasnummer",
                  label: "Pasnummer",
                  content: "AP-0TU2-NR12",
                },
                {
                  id: "valid-until",
                  label: "Geldig tot",
                  content: "23-04-2035",
                },
              ]}
            />
            <div className="rounded-xl p-6 mt-4 border border-grey-border box-shadow-card">
              <h3 className="text-header-5 mb-2">
                <DuoColorText
                  text="Gebruik je Uitpas op jouw manier"
                  emphasis="Uit"
                />
              </h3>
              <Accordion
                bellowType="card"
                items={[
                  {
                    id: "uitpas-wallet-info",
                    title:
                      "Je Iamsterdam Uitpas toevoegen aan Apple Wallet of Google Wallet",
                    content: (
                      <div className="flex flex-col gap-y-4 items-start">
                        <p className="text-body">
                          Wil je je Uitpas makkelijk gebruiken zonder gedoe?
                          Voeg &apos;m toe aan je Apple Wallet of Google Wallet!
                          In de bevestigingsmail na je aankoop vind je een link
                          om je Uitpas eenvoudig toe te voegen aan Apple Wallet
                          of Google Wallet.
                        </p>
                        <p className="text-body">
                          Mail niet meer terug te vinden? Geen probleem! Je kunt
                          de e-mail altijd opnieuw versturen.
                        </p>
                        <Button
                          variant="outline"
                          label="Stuur de e-mail opnieuw"
                        >
                          Stuur de e-mail opnieuw
                        </Button>
                      </div>
                    ),
                    variant: "card",
                  },
                ]}
                className="mt-4"
              />
            </div>
          </section>
        </div>
        <div className="layout-divider" />
        {Boolean(content?.length) && (
          <DynamicContent content={content} parentPageId={undefined} />
        )}
      </div>
    </div>
  );
};

AccountSettingsPageTemplate.propTypes = {
  accountSettingsPage: shape({
    id: string.isRequired,
    title: string.isRequired,
    slug: string.isRequired,
    content: arrayOf(any),
  }).isRequired,
};

export default AccountSettingsPageTemplate;
